import type { ChatRoomItemMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import type { ChatItemMessage } from "@/Modules/Chat/Models/Api/Chat";
import { type AllChatRoomItemType, ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";

import { getChatEvents } from "@/Services/TherapyApi/Chat";

import { useChatStore, type ChatStore } from "@/Modules/Chat/Store";

import { isApp } from "@/Utils/Ionic";
import { hasValue } from "@/Utils/Objects";

import CONFIG from "@/Modules/Chat/Config";

function getPageSize(unreadEvents: number, minEventsNumber: number): number {
  if (unreadEvents < minEventsNumber) return minEventsNumber;

  let pageSize = minEventsNumber;

  while (pageSize < unreadEvents) {
    pageSize += minEventsNumber;
  }

  return pageSize;
}

function getNumEventsRaw(events: AllChatRoomItemType[]): number {
  return events.filter((ev) => ev.type !== ChatRoomItemType.NEUTRAL_TIME_TEXT).length;
}

function calculatePage(currentNumEvents: number, pageSize: number) {
  if (currentNumEvents % pageSize === 0) {
    return currentNumEvents / pageSize;
  } else {
    return Math.floor(currentNumEvents / pageSize);
  }
}

function cleanEventList(currentNumEvents: number, pageSize: number, events: ChatItemMessage[]) {
  if (currentNumEvents % pageSize === 0) {
    return events;
  } else if (events.length === 0) {
    return [];
  } else {
    const currentPage = Math.floor(currentNumEvents / pageSize);
    const totalElements = currentPage * pageSize;
    const elementsDiff = currentNumEvents - totalElements;

    return events.splice(elementsDiff, events.length);
  }
}

const ChatGetEventsActionHandler = async (
  chatId: string,
  unreadEvents?: number
): Promise<ChatRoomItemMessageWebSocket[] | undefined> => {
  const chatStore: ChatStore = useChatStore();

  const room = chatStore.rooms[chatId];
  const totalEvents = getNumEventsRaw(room.events);

  if (room.events.length === 0) {
    chatStore.rooms[chatId].isLoadingEvents = true;
  }

  let pageSize = isApp() ? CONFIG.CHAT_EVENT_PAGE_SIZE_APP : CONFIG.CHAT_EVENT_PAGE_SIZE;

  if (hasValue(unreadEvents) && unreadEvents > 0) {
    pageSize = getPageSize(unreadEvents, pageSize);
  }

  const currentPage = calculatePage(totalEvents, pageSize);
  const messages = await getChatEvents(room.id, currentPage, pageSize)
    .catch(() => {
      return undefined;
    })
    .then((events) => {
      if (!hasValue(events)) return undefined;

      return cleanEventList(totalEvents, pageSize, events);
    });

  if (!hasValue(messages)) {
    chatStore.rooms[chatId].isLoadingEvents = false;

    return undefined;
  }

  await Promise.all(messages.map((message) => chatStore.processEventMessage({ ...message, old: true })));

  chatStore.rooms[chatId].isLoadingEvents = false;

  return messages;
};

export default ChatGetEventsActionHandler;
